import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { register, selectAuthLoading, selectAuthError, selectRequiresVerification, selectVerificationMessage, clearError } from '../store/slices/authSlice';
import LoadingSpinner from '../components/LoadingSpinner';
import { ThemeToggle } from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';
import './Auth.css';

// Toggle this to enable/disable public signups
const INVITE_ONLY_MODE = true;

const Signup: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [validationError, setValidationError] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);
  const requiresVerification = useAppSelector(selectRequiresVerification);
  const verificationMessage = useAppSelector(selectVerificationMessage);
  const { theme } = useTheme();

  const logoLarge = theme === 'dark'
    ? `${process.env.PUBLIC_URL}/debriefr_logo_text_dark.png`
    : `${process.env.PUBLIC_URL}/debriefr_logo_text_transparant.png`;

  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');

    if (formData.password !== formData.confirmPassword) {
      setValidationError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return;
    }

    try {
      dispatch(clearError());
      const result = await dispatch(register({
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName
      }));

      if (register.fulfilled.match(result)) {
        navigate('/verify-email', { state: { email: formData.email } });
      }
    } catch (error) {
      console.error('Registration failed:', error);
    }
  };

  return (
    <div className="auth-container">
      <div className="theme-toggle-container">
        <ThemeToggle />
      </div>
      <div className="auth-card">
        {requiresVerification ? (
          <>
            <h1>Check Your Email</h1>
            <p>We've sent you a verification link</p>

            <div className="alert alert-success" style={{ marginBottom: '20px' }}>
              {verificationMessage}
            </div>

            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <p>Please check your email and click the verification link to activate your account.</p>
              <p><small>Can't find the email? Check your spam folder.</small></p>
            </div>

            <div className="auth-footer">
              <p>Already verified? <Link to="/login">Sign in</Link></p>
              <p>Didn't receive an email? <Link to="/resend-verification">Resend verification</Link></p>
            </div>
          </>
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <img
                src={logoLarge}
                alt="Debriefr"
                style={{ width: '200px', maxWidth: '100%' }}
              />
            </div>
            <h1>Get Started</h1>
            <p>Create your Debriefr account</p>

            {validationError && (
              <div className="alert alert-error">
                {validationError}
              </div>
            )}

            {error && (
              <div className="alert alert-error">
                {error.includes(';') ? (
                  <div>
                    <p><strong>Please fix the following issues:</strong></p>
                    <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                      {error.split(';').map((err, index) => (
                        <li key={index} style={{ marginBottom: '4px' }}>
                          {err.trim().replace(/^[^:]+:\s*/, '')}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  error
                )}
              </div>
            )}

            {INVITE_ONLY_MODE && (
              <div style={{
                backgroundColor: '#4f46e5',
                color: '#ffffff',
                padding: '16px 20px',
                borderRadius: '8px',
                textAlign: 'center',
                marginBottom: '20px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}>
                <div style={{ fontSize: '1.1em', fontWeight: '600', marginBottom: '4px', color: '#ffffff' }}>
                  Currently by invite only
                </div>
                <div style={{ fontSize: '0.9em', color: 'rgba(255,255,255,0.9)' }}>
                  Launching soon!
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form" style={INVITE_ONLY_MODE ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
              <div className="form-group">
                <label htmlFor="fullName">Full Name</label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  disabled={isLoading || INVITE_ONLY_MODE}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={isLoading || INVITE_ONLY_MODE}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  disabled={isLoading || INVITE_ONLY_MODE}
                  required
                />
                <small style={{ color: '#666', fontSize: '0.85em', marginTop: '4px', display: 'block' }}>
                  Must be at least 8 characters with uppercase, lowercase, and number
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  disabled={isLoading || INVITE_ONLY_MODE}
                  required
                />
              </div>

              <button type="submit" className="btn btn-primary btn-full" disabled={isLoading || INVITE_ONLY_MODE}>
                {isLoading ? <LoadingSpinner size="sm" color="white" /> : 'Create Account'}
              </button>
            </form>

            <div className="auth-footer">
              <p>Already have an account? <Link to="/login">Sign in</Link></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Signup;